import React, { useMemo, useState } from 'react';
import { Button, Card, Col, Row, Select, Space, Statistic, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { useEmployeeOptions } from './shared/useEmployeeOptions';
import { hrmsStatusColor } from './shared/hrmsStatus';

const { Text } = Typography;

const LoansPage: React.FC = () => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<string>();
  const { empLabel } = useEmployeeOptions();

  const { data: rows = [], isLoading } = useQuery({
    queryKey: hrmsKeys.loans(),
    queryFn: () => hrmsService.listLoans(),
  });

  const filtered = useMemo(
    () => (statusFilter ? rows.filter((r: any) => r.status === statusFilter) : rows),
    [rows, statusFilter]
  );

  const stats = useMemo(() => {
    const outstanding = rows.reduce((s: number, r: any) => s + Number(r.outstanding_amount || 0), 0);
    const active = rows.filter((r: any) => r.status === 'Active' || r.status === 'Approved').length;
    return { total: rows.length, active, outstanding };
  }, [rows]);

  const columns: ColumnsType<any> = [
    {
      title: 'Loan #',
      dataIndex: 'loan_number',
      width: 130,
      fixed: 'left',
      render: (v: string, r) => <a onClick={() => navigate(`/hr/loans/${r.id}`)}>{v || 'Open'}</a>,
    },
    { title: 'Employee', dataIndex: 'employee_id', ellipsis: true, render: (id: string) => empLabel(id) },
    { title: 'Type', dataIndex: 'loan_type', width: 110 },
    { title: 'Principal', dataIndex: 'principal_amount', width: 110 },
    { title: 'Outstanding', dataIndex: 'outstanding_amount', width: 120 },
    { title: 'EMI', dataIndex: 'installment_amount', width: 100 },
    {
      title: 'Installments',
      key: 'inst',
      width: 110,
      render: (_, r) => `${r.installments_paid ?? 0}/${r.installments_total ?? 0}`,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 110,
      render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag>,
    },
    {
      title: 'Actions',
      key: 'a',
      fixed: 'right',
      width: 90,
      render: (_, r) => (
        <Button type="link" size="small" icon={<EyeOutlined />} onClick={() => navigate(`/hr/loans/${r.id}`)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Employee loans"
          subtitle="Loans with installment schedules for payroll deduction"
          primaryActions={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/loans/new')}>
              New loan
            </Button>
          }
        />
      }
    >
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Total loans" value={stats.total} /></Card></Col>
        <Col xs={12} sm={8}><Card size="small"><Statistic title="Active" value={stats.active} /></Card></Col>
        <Col xs={24} sm={8}><Card size="small"><Statistic title="Outstanding" value={stats.outstanding} precision={2} /></Card></Col>
      </Row>
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space>
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 160 }}
            value={statusFilter}
            onChange={setStatusFilter}
            options={['Draft', 'Pending', 'Active', 'Approved', 'Closed', 'Cancelled'].map((v) => ({
              value: v,
              label: v,
            }))}
          />
          <Text type="secondary">{filtered.length} record(s)</Text>
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isLoading}
        columns={columns}
        dataSource={filtered}
        scroll={{ x: 1100 }}
        pagination={{ pageSize: 20 }}
        onRow={(r) => ({ onClick: () => navigate(`/hr/loans/${r.id}`), style: { cursor: 'pointer' } })}
      />
    </WorkspaceLayout>
  );
};

export default LoansPage;
